import { type ReactNode, useCallback, useId, useMemo } from 'react';
import { type Editor, EditorContent } from '@tiptap/react';
import { cn } from '@shadcn/lib/utils';
import { RichTextToolbar } from 'components/richText';
import { useRichTextField } from 'hooks/useRichTextField';
import { toBuildingBlockDoc } from './placeholders/buildingBlockContent';
import {
  Field,
  FieldHelper,
  FieldLabel,
  FieldToolbarRow,
  TEXT_BUILDING_BLOCK_FIELD_CLASS,
} from './TextBuildingBlockTextarea.styles';
import {
  useTextBuildingBlockEditing,
  useTextBuildingBlockSetup,
} from './useTextBuildingBlockEditing';

interface TextBuildingBlockTextareaProps {
  section: string;
  label: string;
  helper?: string;
  value: string;
  onValueChange: (value: string) => void;
  id?: string;
  placeholder?: string;
  className?: string;
  renderActions?: (editor: Editor) => ReactNode;
}

export const TextBuildingBlockTextarea = ({
  section,
  label,
  helper,
  value,
  onValueChange,
  id,
  placeholder,
  className,
  renderActions,
}: TextBuildingBlockTextareaProps) => {
  const labelId = useId();
  const setup = useTextBuildingBlockSetup();

  const attributes = useMemo(
    () => ({
      ...(id ? { id } : {}),
      'aria-labelledby': labelId,
      role: 'textbox',
      'aria-multiline': 'true',
      class: cn(TEXT_BUILDING_BLOCK_FIELD_CLASS, className),
    }),
    [id, labelId, className]
  );
  const parseValue = useCallback((html: string) => toBuildingBlockDoc(html), []);

  const { editor } = useRichTextField({
    value,
    onChange: onValueChange,
    placeholder,
    attributes,
    extraExtensions: setup.extensions,
    parseValue,
    onKeyDown: setup.onKeyDown,
  });

  const { containerRef, dropdown, isToolbarDisabled } = useTextBuildingBlockEditing({
    section,
    editor,
    setup,
  });

  return (
    <Field>
      <FieldLabel id={labelId} htmlFor={id}>
        {label}
      </FieldLabel>
      <FieldToolbarRow>
        {helper ? <FieldHelper>{helper}</FieldHelper> : null}
        <RichTextToolbar editor={editor} label={label} disabled={isToolbarDisabled} />
        {renderActions?.(editor)}
      </FieldToolbarRow>
      <div className="relative" ref={containerRef}>
        <EditorContent editor={editor} />
        {dropdown}
      </div>
    </Field>
  );
};
